import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getHanko, isAuthenticated } from '@/auth/hanko';

// No explicit "create account" step needed here: PortalAccount auto-provisioning
// (CurrentAccountAccessor.GetOrProvisionCurrentAccountAsync, BILLING_PLAN.md §7.2) happens
// server-side on the first authenticated Portal-API call (see src/api/hooks.ts useInstances()
// etc., called once the authenticated shell mounts).
interface SessionState {
  status: 'loading' | 'authenticated' | 'anonymous';
}

const SessionContext = createContext<SessionState>({ status: 'loading' });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');

  useEffect(() => {
    void isAuthenticated().then((authed) => setStatus(authed ? 'authenticated' : 'anonymous'));

    // Belt-and-braces session-expiry handling: without this a user idle on a protected route
    // doesn't notice the Hanko cookie aged out until the next hard reload.
    const unsubscribers: Array<() => void> = [];
    try {
      const hanko = getHanko();
      unsubscribers.push(hanko.onSessionExpired(() => setStatus('anonymous')));
      unsubscribers.push(hanko.onUserLoggedOut(() => setStatus('anonymous')));
    } catch {
      // Hanko not initialised (missing VITE_HANKO_API_URL) — login won't work anyway.
    }
    return () => unsubscribers.forEach((unsub) => unsub());
  }, []);

  return <SessionContext.Provider value={{ status }}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

/**
 * Route-guard used from `beforeLoad` on protected routes (see routes/_authenticated.tsx).
 * Re-checks the Hanko session directly rather than trusting React state, since `beforeLoad`
 * can run before <SessionProvider> has resolved its first check.
 */
export async function requireSession(): Promise<void> {
  const authed = await isAuthenticated();
  if (!authed) {
    throw new Error('unauthenticated');
  }
}
